import type { Feature, FeatureCollection } from "geojson";
import { type ComponentType, useEffect, useState } from "react";

import { useUi } from "../../state/ui";
import { usePropagation } from "./propagation";
import { useDeployment } from "./useDeployment";

/** Mise en évidence des unités touchées par une propagation (docs/25 §6) : halo qui pulse trois fois sous leur
 *  symbole (bleu ami, rouge ennemi, couleurs APP-6) et trait discontinu depuis l'unité cause, qui s'effacent en
 *  `PULSE_MS` ; une ligne cliquée de l'avis remet son unité en évidence. Les nouveaux symboles de mission, eux, se
 *  fondent par la transition commune des symboles (`MissionSymbolsLayers`). Mouvement réduit : un seul halo fixe. */

export const PULSE_MS = 2600;
const PULSES = 3;

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
}

function reducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/** Avancement du halo (0 à 1 dans chaque pulsation) et opacité d'ensemble au temps `elapsed` (ms). */
export function pulseAt(elapsed: number, reduced = false): { ring: number; fade: number } {
  // Horloge du rendu antérieure à la mise en évidence (premier rendu) : début de la pulsation.
  elapsed = Math.max(0, elapsed);
  if (elapsed >= PULSE_MS) return { ring: 1, fade: 0 };
  const fade = 1 - Math.max(0, (elapsed - PULSE_MS * 0.7) / (PULSE_MS * 0.3));
  if (reduced) return { ring: 0.5, fade };
  const period = PULSE_MS / PULSES;
  return { ring: (elapsed % period) / period, fade };
}

export function PropagationLayers({ Source, Layer }: EngineParts) {
  const focus = usePropagation((s) => s.focus);
  const { data } = useDeployment();
  const phase = useUi((s) => s.phase);
  const [now, setNow] = useState(() => performance.now());

  useEffect(() => {
    if (!focus) return;
    let frame = 0;
    const loop = () => {
      setNow(performance.now());
      if (performance.now() - focus.at < PULSE_MS) frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [focus]);

  if (!focus || !data) return null;
  const elapsed = now - focus.at;
  if (elapsed >= PULSE_MS) return null;
  const { ring, fade } = pulseAt(elapsed, reducedMotion());
  const units = new Map(data.units.map((u) => [u.key, u]));
  const point = (key: string | null): [number, number] | null => {
    const p = key ? units.get(key)?.positions[phase] : undefined;
    return p ? [p.lon, p.lat] : null;
  };
  const cause = point(focus.cause);
  const halos: Feature[] = [];
  const links: Feature[] = [];
  for (const key of focus.keys) {
    const at = point(key);
    if (!at) continue;
    const side = units.get(key)?.side ?? "AMI";
    halos.push({ type: "Feature", geometry: { type: "Point", coordinates: at }, properties: { side } });
    if (cause && key !== focus.cause) {
      links.push({ type: "Feature", geometry: { type: "LineString", coordinates: [cause, at] }, properties: { side } });
    }
  }
  const color = ["match", ["get", "side"], "ENI", "#ff3b30", "#5cbdff"];
  return (
    <>
      <Source id="gx-propagation-links" type="geojson" data={{ type: "FeatureCollection", features: links } as FeatureCollection}>
        <Layer
          id="propagation-links"
          type="line"
          beforeId="units-moved"
          paint={{ "line-color": color, "line-width": 1.6, "line-dasharray": [2, 2], "line-opacity": 0.85 * fade }}
        />
      </Source>
      <Source id="gx-propagation-halos" type="geojson" data={{ type: "FeatureCollection", features: halos } as FeatureCollection}>
        <Layer
          id="propagation-halos"
          type="circle"
          beforeId="units-moved"
          paint={{
            "circle-radius": 24 + 20 * ring,
            "circle-color": color,
            "circle-opacity": 0.28 * (1 - ring) * fade,
            "circle-stroke-color": color,
            "circle-stroke-width": 2,
            "circle-stroke-opacity": 0.9 * (1 - ring) * fade,
          }}
        />
      </Source>
    </>
  );
}
